// What a refusal from the approval API means, in words.
//
// THE SERVER'S MESSAGE DOES NOT REACH PROD. `error_response` in cres-aws does
// `if ssm.get('ENV') not in ['prod']: cause = e.message`, so outside development the body carries
// only `Code`. Every carefully written refusal -- "A draft of the matrix is already in progress",
// "You have already signed this approval" -- arrives as nothing, and the caller shows a generic
// fallback.
//
// That matters more here than elsewhere because THE REFUSALS ARE THE PRODUCT. "You have already
// signed this" and "nobody available can sign for Quality" are what make the control
// comprehensible; reduced to "could not save", they teach people the feature is broken.
//
// Mapped client-side rather than by changing the platform's behaviour: suppressing internal detail
// in prod is a deliberate choice and not one to reverse for one feature. These are OUR codes and
// OUR wording, so nothing internal leaks by writing them down here.
//
// FOUNDATION'S CODES, NOT THE CATALOG'S. This file arrived from cres-t5 keyed on CC-OAMX-*,
// CC-APRV-* and CC-GVCL-*, none of which Foundation raises -- it raises CC-OOAM-* and CC-OAPR-*.
// Every entry matched nothing, which is invisible in development (the server still sends `Cause`)
// and total in prod, where it strips it. A mapping table is only worth having if its keys are the
// keys that arrive, so any code added to the services belongs here too.

// Exported so a test can assert the KEYS are the codes Foundation raises.
export const MESSAGES: Record<string, string> = {
    // Matrix -- CC-OOAM-*
    "CC-OOAM-DRFT": "A draft of the matrix is already in progress. Release or cancel it before revising again.",
    "CC-OOAM-RVSE": "Only a released revision can be revised.",
    "CC-OOAM-CNCL": "Only a draft revision can be cancelled.",
    "CC-OOAM-SEED": "This realm already has an approval matrix.",
    "CC-OOAM-DUPE": "Two rows for the same object type \u2014 one of them would be picked arbitrarily.",
    "CC-OOAM-QUOR": "A quorum group asks for more signatures than it has approvers, so it can never be satisfied.",
    "CC-OOAM-MODE": "That approver cannot be set from here yet.",
    "CC-OOAM-MODE2": "Organization and Management describe how far a query reads, not a person. A signature has to name somebody.",
    "CC-OOAM-WHO": "Every approver slot has to name a persona.",
    "CC-OOAM-MEAN": "That is not a meaning a signature can carry.",
    "CC-OOAM-TYPE": "That record type cannot be governed by approvals.",
    "CC-OOAM-ACTN": "That is not something you can do to a matrix revision.",
    "CC-OOAM-404": "That matrix revision no longer exists.",
    "CC-OOAM-PAYL": "That request could not be read.",

    // Requests and signatures -- CC-OAPR-*
    "CC-OAPR-REQD": "This record needs approval before it can be released. Submit it for approval.",
    "CC-OAPR-NONE": "No approvers are configured for this object type, so it releases directly.",
    "CC-OAPR-NOMX": "This realm has no approval matrix.",
    "CC-OAPR-OPEN": "This revision already has an approval in progress.",
    "CC-OAPR-APRV": "This revision is already approved and is waiting to be released. Editing it marks the approval stale, and then it can be submitted again.",
    "CC-OAPR-RTRN": "This record requires retraining, so submitting it needs an answer: does this change require people to retrain?",
    "CC-OAPR-DRFT": "Only a draft revision can be sent for approval.",
    "CC-OAPR-SUGG": "This document has tracked changes that nobody has accepted or rejected yet. Resolve them before sending it for approval -- a signature has to be against one version of the text, not two.",
    "CC-OAPR-SHUT": "This approval has already been decided.",
    "CC-OAPR-STAGE": "That stage is not open yet.",
    "CC-OAPR-DONE": "That slot is already signed.",
    "CC-OAPR-SLOT": "That slot is not part of this approval.",
    "CC-OAPR-STAL": "This record changed after it was submitted, so the signatures no longer cover what is proposed. Submit it again.",
    "CC-OAPR-ELIG": "You are not acting for the persona this slot is signed by.",
    "CC-OAPR-AUTH": "The person who authored a record does not approve it.",
    "CC-OAPR-DIST": "You have already signed this approval. A second signature from the same person is not a second review.",
    "CC-OAPR-DECN": "A signature approves or rejects.",
    "CC-OAPR-CMNT": "A rejection has to say why.",
    "CC-OAPR-OWNR": "Only the person who submitted it can withdraw it.",
    "CC-OAPR-WHO": "A signature names a person, and you could not be identified.",
    "CC-OAPR-404": "That revision no longer exists.",
    "CC-OAPR-404R": "That approval request no longer exists.",
    "CC-OAPR-TYPE": "That record type cannot be governed by approvals.",
    "CC-OAPR-USID": "That request was missing the record it refers to.",
    "CC-OAPR-PAYL": "That request could not be read.",

    // Change orders. Nearly every refusal in that service is user_facing, so the server's own
    // sentence -- which names the state the order is actually in -- normally wins over these. They
    // are the floor for the one that is not, and for anything a future release stops sending.
    "CC-CORD-NONE": "That change order no longer exists.",
    "CC-CORD-FRZN": "This change order can no longer be changed.",
    "CC-CORD-EDIT": "A change order can only be edited while it is a draft.",
    "CC-CORD-MFRZ": "Records can only be added or removed while the change order is a draft.",
    "CC-CORD-CLOS": "Only an approved change order can be closed.",
    "CC-CORD-STAT": "A change order's state moves by submitting, signing, closing or voiding it.",
    "CC-CORD-TYPE": "That change order type does not exist.",
    "CC-CORD-MREC": "That record does not exist.",
    "CC-CORT-NONE": "That task no longer exists.",
    "CC-CORT-STAT": "A task is finished either DONE or ABANDONED.",

    // Shared
    "CC-PRIV-DENY": "You do not have permission to do that on this record.",
};

/**
 * The best sentence available for a failed request.
 *
 * Prefers the server's own `Cause` when it is present -- in development it is, and it is always
 * more specific than anything written here, because it can name the slot or the object type that
 * caused the refusal, which no static string can.
 */
export function approvalMessage(error: any, fallback: string) {
    const data = error?.response?.data;
    if (data?.Cause) return data.Cause;
    if (data?.Code && MESSAGES[data.Code]) return MESSAGES[data.Code];
    if (data?.Code) return `${fallback} (${data.Code})`;
    return fallback;
}

export const APPROVAL_MESSAGES = MESSAGES;
